import React, { useState, useEffect, useCallback } from 'react';
import { FiX, FiLoader } from 'react-icons/fi';
import { SETORES, buscarHistoricoCliente, salvarHistoricoCliente } from '../../services/clientesHistorico';
import styles from './ModalHistoricoCliente.module.css';

/* Anotações de particularidades do cliente, uma aba por setor. Quem decide
   as abas visíveis é o backend (admin e gerente veem as quatro; os demais,
   só a do próprio setor), então aqui a gente só renderiza o que veio em
   `setores` -- nunca a lista fixa. */

export default function ModalHistoricoCliente({ cliente, onFechar, onSalvo }) {
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState('');
  const [setores, setSetores] = useState([]);
  const [textos, setTextos] = useState({});       // o que está na tela
  const [originais, setOriginais] = useState({}); // o que veio do banco
  const [meta, setMeta] = useState({});           // quem mexeu e quando
  const [aba, setAba] = useState(null);
  const [setorUsuario, setSetorUsuario] = useState(null);

  useEffect(() => {
    let vivo = true;
    buscarHistoricoCliente(cliente.id)
      .then(({ setores: visiveis, itens, setorUsuario: meuSetor }) => {
        if (!vivo) return;
        setSetorUsuario(meuSetor || null);
        const porSetor = {};
        const infos = {};
        (itens || []).forEach(i => {
          porSetor[i.setor] = i.texto || '';
          infos[i.setor] = { em: i.atualizado_em, por: i.atualizado_por };
        });
        const base = {};
        (visiveis || []).forEach(s => { base[s] = porSetor[s] || ''; });
        setSetores(visiveis || []);
        setTextos(base);
        setOriginais(base);
        setMeta(infos);
        /* abre já na aba que tem anotação -- é o motivo mais comum de abrir */
        setAba((visiveis || []).find(s => base[s]) || (visiveis || [])[0] || null);
      })
      .catch(e => vivo && setErro(e.message))
      .finally(() => vivo && setCarregando(false));
    return () => { vivo = false; };
  }, [cliente.id]);

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onFechar(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onFechar]);

  const alterados = setores.filter(s => (textos[s] || '') !== (originais[s] || ''));

  const salvar = useCallback(async () => {
    if (!alterados.length) return onFechar();
    setSalvando(true);
    setErro('');
    try {
      for (const s of alterados) {
        await salvarHistoricoCliente(cliente.id, s, textos[s] || '');
      }
      /* o ícone do Painel acende/apaga pelo conjunto todo, não só pela aba
         que foi mexida agora */
      onSalvo?.(setores.some(s => (textos[s] || '').trim()));
      onFechar();
    } catch (e) {
      setErro(e.message);
      setSalvando(false);
    }
  }, [alterados, cliente.id, textos, setores, onSalvo, onFechar]);

  const rotulo = id => SETORES.find(s => s.id === id)?.label || id;

  const info = aba && meta[aba]
    ? `Atualizado em ${new Date(meta[aba].em).toLocaleString('pt-BR')}${meta[aba].por ? ` por ${meta[aba].por}` : ''}`
    : '';

  return (
    <div className={styles.fundo}>
      <div className={styles.caixa}>
        <div className={styles.topo}>
          <div>
            <h3 className={styles.titulo}>Histórico do cliente</h3>
            <span className={styles.quem}>{cliente.codigo} — {cliente.nome}</span>
          </div>
          <button type="button" className={styles.fechar} onClick={onFechar} title="Fechar">
            <FiX size={17} />
          </button>
        </div>

        {carregando ? (
          <p className={styles.vazio}><FiLoader className={styles.girando} size={15} /> Carregando…</p>
        ) : !setores.length ? (
          <p className={styles.vazio}>
            {setorUsuario
              ? `Seu setor no cadastro ("${setorUsuario}") não corresponde a nenhuma das abas de histórico (Folha, Fiscal, Conformidade e Contábil). Peça ao administrador para ajustar em Configurações › Usuários.`
              : 'Seu usuário não está vinculado a nenhum setor, então não há aba de histórico pra mostrar. Peça ao administrador pra definir seu setor.'}
          </p>
        ) : (
          <>
            <div className={styles.abas}>
              {setores.map(s => (
                <button
                  key={s}
                  type="button"
                  className={`${styles.aba} ${aba === s ? styles.abaAtiva : ''}`}
                  onClick={() => setAba(s)}
                >
                  {rotulo(s)}
                  {(textos[s] || '').trim() && <span className={styles.pontinho} />}
                </button>
              ))}
            </div>

            <div className={styles.corpo}>
              <textarea
                className={styles.texto}
                value={textos[aba] || ''}
                onChange={e => setTextos(t => ({ ...t, [aba]: e.target.value }))}
                placeholder={`Particularidades deste cliente no setor ${rotulo(aba)}: combinados, prazos próprios, cuidados na apuração…`}
                rows={12}
              />
              {info && <span className={styles.info}>{info}</span>}
            </div>
          </>
        )}

        {erro && <p className={styles.erro}>{erro}</p>}

        <div className={styles.rodape}>
          <button type="button" className={styles.btnLeve} onClick={onFechar}>Cancelar</button>
          <button
            type="button"
            className={styles.btnForte}
            onClick={salvar}
            disabled={salvando || carregando || !setores.length || !alterados.length}
          >
            {salvando ? 'Salvando…' : 'Salvar'}
          </button>
        </div>
      </div>
    </div>
  );
}
